/**
 * Morse — aus dem, was gesprochen wurde, die Zeichen ziehen. (08.10.2026, „Sprechen“ im Reiter Hören)
 *
 * Martin: „Super wäre, wenn ich statt Buchstaben zu drücken Buchstaben aussprechen könnte, während des Trainings.“ Auf
 * Rückfrage: „Beides wäre gut“ — am Stück („K M U R E“) UND Buchstabe für Buchstabe (jedes Antippen ein Stück).
 *
 * Rein: Text (oder Wörter mit Konfidenz) hinein, Zeichen heraus. Keine Seite, kein Mikrofon, kein Netz.
 *
 * Was verstanden wird (in dieser Reihenfolge, das erste Treffende gilt):
 *   1. Zeichenwörter: Punkt, Komma, Fragezeichen, Schrägstrich, Gleichheitszeichen …
 *   2. Ziffernwörter: null … neun, „eins“, „zwo“
 *   3. Merkwörter (Monitor, Motor, Michael … — 16.10.: „Buchstabe ODER Merkwort“), nur als ganzes Wort
 *   3b. Buchstabiernamen: DIN 5009 (Anton Berta Cäsar …), die österreichischen Fassungen (Kaufmann, Xaver) und das
 *      NATO-Alphabet (Alfa Bravo Charlie …)
 *   4. Buchstabennamen, wie man sie spricht: „ka“, „kah“, „em“, „jot“, „zet“ …
 *   5. ein einzelnes Zeichen: „K“, „5“
 *   6. Füllwörter („äh“, „also“, „und“ …): werden überhört
 *   7. mehrere Zeichen in einem Wort („kmure“, „k5“): Zeichen für Zeichen — aber nur, wenn jedes davon zu den Zeichen der
 *      Runde gehört (sonst wäre „also“ ein A-L-S-O)
 * Satzzeichen der Mitschrift („K. M. U.“, Kommas, Bindestriche) zählen nicht; das GESPROCHENE Wort „Punkt“ schon.
 *
 * ⚠️ Die Erkennung ist unsicher — Be, De, Ge und Pe klingen gleich, Em und En auch. Darum wählt dieser Leser nie still
 *    das Falsche: Ein Zeichen aus einem Buchstabennamen (oder einzelnen Buchstaben), dessen Verwechsler in dieser Runde
 *    vorkommen, bleibt LEER (`LUECKE`) und kommt mit seinen Möglichkeiten zurück; ebenso jedes Zeichen, das die Erkennung
 *    selbst nur unsicher hörte (Konfidenz). Buchstabiernamen („Berta“) verwechselt nichts — sie sind der sichere Weg.
 * ⚠️ Der Leser kennt die Zeichen der Runde (`erlaubt`) nur als Hinweis auf Verwechslungen und für Wörter mit mehreren
 *    Zeichen. Ein EINZELNES gesprochenes Zeichen gilt auch, wenn es nicht dazugehört: wer falsch hörte, soll falsch
 *    antworten dürfen (Prior schlägt Messung nie).
 */
import { grossZeichen, MORSE_TABELLE } from "../../shared/morse.js";
import { merkwortBuchstabe } from "../../shared/morse-sprech-merkwoerter.js";

/** Das Platzhalter-Zeichen im Tipp-Feld: hier war ein Zeichen unsicher oder nicht erkannt. Zählt nicht als Zeichen. */
export const LUECKE = "·";

/** Unter dieser Konfidenz (0…1) der Erkennung gilt ein Zeichen als unsicher. Ein Anhaltswert — am Gerät zu messen. */
export const KONFIDENZ_UNSICHER = 0.55;

/** Ein Wort der Mitschrift, wenn die Erkennung seine Sicherheit mitliefert. */
export interface SprachWort {
  text: string;
  konfidenz?: number | null;
}

interface GelesenesZeichen {
  /** Das gewählte Zeichen (Großbuchstabe, Ziffer, Satzzeichen) — `null`: unsicher, bleibt leer. */
  zeichen: string | null;
  /** Nur bei `null`: die Möglichkeiten, die wahrscheinlichste zuerst. */
  moegliche: string[];
}

export interface Gelesen {
  /** Höchstens `laenge` Zeichen, in der Reihenfolge des Gesprochenen. */
  zeichen: GelesenesZeichen[];
  /** Das Stück fürs Tipp-Feld: die Zeichen, an unsicheren Stellen `LUECKE`. */
  feld: string;
  /** Wie viele Zeichen über die Länge hinaus gesprochen wurden (und weggelassen sind). */
  abgeschnitten: number;
  /** Wörter, aus denen nichts zu lesen war (Füllwörter nicht mitgezählt). */
  unverstanden: string[];
  /** Kein einziges Zeichen aus dem Text gezogen. */
  leer: boolean;
  /** Wie viele Stellen unsicher sind (= Anzahl der `LUECKE` im Feld). */
  luecken: number;
}

/* ── Die Tabellen ─────────────────────────────────────────────────── */

/** Buchstabiernamen: DIN 5009, österreichische Fassung, NATO. Schreibweisen, wie eine Erkennung sie liefert. */
export const BUCHSTABIERNAMEN: Readonly<Record<string, string>> = {
  anton: "A", berta: "B", caesar: "C", cäsar: "C", cesar: "C", dora: "D", emil: "E", friedrich: "F", gustav: "G",
  heinrich: "H", ida: "I", julius: "J", kaufmann: "K", karl: "K", konrad: "K", ludwig: "L", martha: "M", marta: "M",
  nordpol: "N", nord: "N", otto: "O", paula: "P", quelle: "Q", richard: "R", samuel: "S", siegfried: "S", theodor: "T",
  ulrich: "U", viktor: "V", victor: "V", wilhelm: "W", xanthippe: "X", xantippe: "X", xaver: "X", ypsilon: "Y",
  zacharias: "Z", zeppelin: "Z",
  aerger: "Ä", ärger: "Ä", oekonom: "Ö", ökonom: "Ö", uebermut: "Ü", übermut: "Ü", eszett: "ß",
  // NATO
  alpha: "A", alfa: "A", bravo: "B", charlie: "C", delta: "D", echo: "E", foxtrot: "F", golf: "G", hotel: "H", india: "I",
  juliet: "J", juliett: "J", kilo: "K", lima: "L", mike: "M", november: "N", oscar: "O", oskar: "O", papa: "P", quebec: "Q",
  romeo: "R", sierra: "S", tango: "T", uniform: "U", whiskey: "W", whisky: "W", xray: "X", yankee: "Y", zulu: "Z",
};

/**
 * Buchstabennamen, wie man sie spricht — mit den Schreibweisen, in denen sie in einer Mitschrift stehen.
 *
 * 15.10.2026 (Martin, „Augen zu“: „K gesagt, es war K — nicht erkannt“): Ein einzeln gesprochener Buchstabe kommt aus der
 * Erkennung als Wort („Kah“, „Käh“, „Kay“, „Car“), als Laut mit Umlaut („bäh“, „tä“) oder englisch („Jay“, „Zed“, „Kju“).
 * Jede Schreibweise steht HIER als Zeile — keine Muster, kein `\b` (kennt keine Umlaute). ⚠️ Nichts aus `FUELLWOERTER`
 * („äh“, „ehm“, „zu“, „ja“) und kein häufiges Wort („du“, „see“) — das wäre in jedem Satz ein Buchstabe.
 */
export const BUCHSTABENNAMEN: Readonly<Record<string, string>> = {
  ah: "A", aa: "A", aah: "A", ahh: "A",
  be: "B", beh: "B", bee: "B", bä: "B", bäh: "B",
  ce: "C", ze: "C", tse: "C", tze: "C", cee: "C", zeh: "C", tseh: "C", tsee: "C",
  de: "D", deh: "D", dee: "D", dä: "D", däh: "D",
  eh: "E", ee: "E",
  ef: "F", eff: "F", äf: "F", äff: "F",
  ge: "G", geh: "G", gee: "G", gä: "G", gäh: "G",
  ha: "H", hah: "H", haa: "H",
  ih: "I", ii: "I", ie: "I", ieh: "I",
  jot: "J", jott: "J", yot: "J", yott: "J", jod: "J", jay: "J", yod: "J", jey: "J",
  ka: "K", kah: "K", kaa: "K", kaah: "K", ca: "K", cah: "K", caa: "K", kha: "K", kä: "K", käh: "K", kay: "K", kar: "K", car: "K",
  kahh: "K", khaa: "K", khah: "K", caah: "K", kaye: "K",
  el: "L", ell: "L", äl: "L", äll: "L",
  em: "M", emm: "M", äm: "M", ämm: "M",
  en: "N", enn: "N", än: "N", änn: "N",
  oh: "O", oo: "O", ooh: "O",
  pe: "P", peh: "P", pee: "P", pä: "P", päh: "P",
  ku: "Q", kuh: "Q", kuu: "Q", qu: "Q", kju: "Q", kuuh: "Q", quh: "Q",
  er: "R", err: "R", är: "R", ärr: "R", ähr: "R", ehr: "R",
  es: "S", ess: "S", äs: "S", äss: "S", eß: "S",
  te: "T", teh: "T", tee: "T", tä: "T", täh: "T",
  uh: "U", uu: "U", uuh: "U", uhh: "U", ou: "U", ouh: "U",
  vau: "V", fau: "V", fao: "V", vao: "V", vauh: "V", fauh: "V",
  we: "W", weh: "W", wee: "W", ve: "W", veh: "W", vee: "W", wä: "W", wäh: "W",
  ix: "X", iks: "X", ixx: "X", icks: "X",
  üpsilon: "Y", ypsilon: "Y", ipsilon: "Y", upsilon: "Y", ypsylon: "Y",
  zet: "Z", zett: "Z", tset: "Z", tsett: "Z", zäd: "Z", zät: "Z", zätt: "Z", zed: "Z", tsät: "Z", tsätt: "Z",
  üh: "Ü",
};

/**
 * Fehlhörungen EINES einzeln gesprochenen Buchstabens — pro Buchstabe, die wahrscheinlichste zuerst. (21.10.2026, Martin:
 * „U“ gesagt, „jedes zweite Mal nicht erkannt“.) Die Erkennung (Soniox, deutsch mit Englisch im Ohr) schreibt eine einzelne
 * Silbe gern als Wort: „U“ → „you“/„und“/„uhr“, „R“ → „herr“/„her“, „K“ → „ja“/„ga“. Journal 16.–21.10.: `wort=herr`,
 * `wort=ja` bei R und K.
 *
 * ⚠️ NIE ein sicherer Treffer: diese Wörter sind echte Wörter („und“, „ja“, „her“). Sie gelten nur, wenn die ganze Äußerung
 *    aus EINEM Wort besteht, und nur als Vorschlag (Augen zu: Tippfelder; Sprechen: Lücke mit Möglichkeiten). Kein Eintrag,
 *    der schon in BUCHSTABENNAMEN/BUCHSTABIERNAMEN steht (dort ist er sicher).
 */
export const EINZEL_FEHLHOERUNGEN: Readonly<Record<string, readonly string[]>> = {
  A: ["aha", "ach", "har"],
  E: ["he", "ehe"],
  I: ["hi", "ihr"],
  K: ["ja", "ga", "kai", "kahn", "cha"],
  L: ["elle", "hell", "all"],
  M: ["ihm", "emma", "hem", "im"],
  N: ["an", "in", "end", "änne"],
  O: ["ohr", "hoh", "oho"],
  R: ["herr", "her", "air", "ar", "arr", "are", "eher", "ehre"],
  S: ["is", "eis", "ist"],
  T: ["the", "tea", "die"],
  U: ["you", "und", "uhr", "du", "hu", "huh", "ju"],
  // 23.10.2026 (Prüfer-Auflage): englische und lautschriftliche Formen nur als Tippfeld — als Name zählten „ex“/„ecks“ bei nicht
  // gelerntem X für S (gleicher Selbstlaut), „why“ ist ein englisches Wort.
  X: ["ex", "eks", "ecks"],
  Y: ["why", "wai"],
  Q: ["cue"],
};

/** Die Tabelle umgedreht: Wort → Buchstaben (ein Wort kann zu mehreren gehören). */
const FEHLHOERUNG_ZU: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const [z, woerter] of Object.entries(EINZEL_FEHLHOERUNGEN)) for (const w of woerter) m.set(w, [...(m.get(w) ?? []), z]);
  return m;
})();

/** Welche Buchstaben ein einzeln gesprochenes Wort meinen könnte (Fehlhörung) — leer, wenn keine. */
export function fehlhoerung(t: string): readonly string[] {
  return FEHLHOERUNG_ZU.get(t.normalize("NFC").toLowerCase()) ?? [];
}

const ZIFFERNWOERTER: Readonly<Record<string, string>> = {
  null: "0", eins: "1", zwei: "2", zwo: "2", zwoo: "2", drei: "3", vier: "4", fünf: "5", fuenf: "5", funf: "5", sechs: "6",
  sieben: "7", acht: "8", neun: "9",
  // 23.10.2026: wie man sie im Dialekt und schnell spricht (Martin ist Österreicher).
  zwoa: "2", fümf: "5", fümfe: "5", fünfe: "5", siebn: "7", achte: "8", neune: "9",
};

const ZEICHENWOERTER: Readonly<Record<string, string>> = {
  punkt: ".", komma: ",", fragezeichen: "?", schrägstrich: "/", schraegstrich: "/", slash: "/", gleichheitszeichen: "=",
  doppelpunkt: ":", semikolon: ";", strichpunkt: ";", ausrufezeichen: "!", ausrufungszeichen: "!",
  bindestrich: "-", gedankenstrich: "-", minus: "-", plus: "+", apostroph: "'", unterstrich: "_",
  anführungszeichen: '"', anfuehrungszeichen: '"', klammeraffe: "@",
};

/** Was man beim Sprechen nebenbei sagt — wird überhört. ⚠️ Keine Buchstabennamen („es“, „er“, „ha“, „oh“) hier. */
export const FUELLWOERTER: ReadonlySet<string> = new Set([
  "äh", "ähm", "ahm", "ehm", "öhm", "hm", "hmm", "mhm", "also", "und", "dann", "danach", "ja", "nee", "nein", "ok", "okay",
  "bitte", "ich", "glaube", "glaub", "denke", "denk", "meine", "vielleicht", "das", "dies", "ist", "war", "sind", "der", "die",
  "den", "dem", "ein", "eine", "noch", "mal", "nochmal", "halt", "eben", "genau", "gut", "so", "jetzt", "hier", "buchstabe",
  "buchstaben", "zeichen", "gruppe", "wort", "antwort", "lautet", "heißt", "heisst", "wie", "wiederhole", "nämlich", "naja",
  "na", "oder", "doch", "schon", "weiß", "nicht", "kein", "habe", "hab", "gehört", "moment", "sorry", "stopp", "weiter",
  "fertig", "ende", "danke", "ray", "pol", "klammer", "auf", "zu",
]);

/**
 * Was die Erkennung beim Hören eines Buchstabennamens gern für einen anderen hält (be/de/ge/pe/te, em/en, ef/es …).
 * Nachbarn, nicht Gruppen: jedes Zeichen hat seine eigenen Verwechsler.
 */
export const VERWECHSLUNG: Readonly<Record<string, readonly string[]>> = {
  B: ["D", "G", "P", "T"], D: ["B", "G", "T", "P"], G: ["B", "D", "K"], P: ["B", "T", "D"], T: ["P", "D", "B", "K"],
  K: ["G", "Q", "T"], Q: ["K"], M: ["N"], N: ["M", "L"], L: ["N"], F: ["S", "V"], S: ["F", "X"], X: ["S"], V: ["W", "F"],
  W: ["V"], C: ["Z"], Z: ["C"], E: ["I"], I: ["E"], O: ["U"], U: ["O"],
};

/** Der erste Selbstlaut eines Wortes („kah“ → a, „geh“ → e, „kä“ → ä) — leer, wenn keiner. */
function selbstlaut(t: string): string {
  return /[aeiouyäöü]/u.exec(t)?.[0] ?? "";
}

/** Die Buchstabennamen je Zeichen (umgedreht) — für den Klangvergleich. */
const NAMEN_JE_ZEICHEN: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const [name, z] of Object.entries(BUCHSTABENNAMEN)) m.set(z, [...(m.get(z) ?? []), name]);
  return m;
})();

/**
 * Kann das gehörte Wort `t` auch der Buchstabe `n` gewesen sein — klingt es wie einer SEINER Namen? (23.10.2026, Martin:
 * „oft Probleme, dass er den Buchstaben erkennt, wenn es ausgesprochen wird, z. B. K“.)
 *   - Ein bloßer Buchstabe („K“, „g“): ja — die Erkennung hat den Laut schon zum Zeichen gemacht, K und G liegen da nah.
 *   - Ein ausgeschriebener Name („kah“): nur, wenn `n` einen Namen mit DEMSELBEN Selbstlaut hat. „Kah“ hat die Erkennung als
 *     /ka/ gehört — G („geh“), T („teh“) und Q („kuh“) klingen anders und sind keine Verwechsler; „kä“ dagegen liegt neben
 *     „gä“ und „tä“, „em“ neben „en“, „be“ neben „de“, „ge“, „pe“, „te“.
 * Bis dahin teilte „kah“ seine Punkte mit G, Q und T — unsicher gehört (Konfidenz 0,35) war K ab der Stufe mit T nur noch
 * „mehrdeutig“ (nochmal) statt richtig.
 */
export function klingtWie(t: string, n: string): boolean {
  if ([...t].length <= 1) return true;
  const v = selbstlaut(t);
  return v !== "" && (NAMEN_JE_ZEICHEN.get(n) ?? []).some((name) => selbstlaut(name) === v);
}

/** Die Verwechsler von `z`, wenn er als Wort `t` gehört wurde (`klingtWie`). */
export function verwechslerVon(z: string, t: string): string[] {
  return (VERWECHSLUNG[z] ?? []).filter((n) => klingtWie(t, n));
}

export type Quelle = "sicher" | "buchstabe";

/* ── Zerlegen ─────────────────────────────────────────────────────── */

export interface Stueck {
  t: string;
  konf: number | null;
}

export function zerlege(eingabe: string | readonly SprachWort[]): Stueck[] {
  const woerter: SprachWort[] = typeof eingabe === "string" ? [{ text: eingabe }] : [...eingabe];
  const aus: Stueck[] = [];
  for (const w of woerter) {
    const konf = typeof w.konfidenz === "number" && Number.isFinite(w.konfidenz) ? w.konfidenz : null;
    for (const t of w.text.normalize("NFC").toLowerCase().split(/[^\p{L}\p{N}]+/u)) if (t) aus.push({ t, konf });
  }
  return aus;
}

/** Nachschlagen. `glatt`: auch ohne Umlaut-Punkte („cäsar“ ↔ „casar“) — nur für Namen, nie für Buchstabennamen („äh“ ist kein „ah“). */
function suche(tabelle: Readonly<Record<string, string>>, t: string, glatt = false): string | undefined {
  // `hasOwnProperty` statt `Object.hasOwn`: ältere iPhones kennen Letzteres nicht — und „constructor“ ist kein Zeichen.
  const hat = (k: string): boolean => Object.prototype.hasOwnProperty.call(tabelle, k);
  if (hat(t)) return tabelle[t];
  if (!glatt) return undefined;
  const ohne = t.normalize("NFD").replace(/\p{M}/gu, "");
  return ohne !== t && hat(ohne) ? tabelle[ohne] : undefined;
}

/**
 * Gedehnt gesprochen schreibt die Erkennung Buchstaben mehrfach („Kaaaa“, „Zettt“, „Ooooh“): drei und mehr gleiche Zeichen
 * in Folge werden zwei („kaa“, „zett“, „ooh“) — die Tabelle kennt die Formen mit zwei. (23.10.2026) Nur für die Buchstabennamen;
 * „mmm“/„hmmm“ sind M-Laute (morse-augen-logik.ts) und stehen hier nicht.
 */
export function gestaucht(t: string): string {
  return t.replace(/(\p{L})\1{2,}/gu, "$1$1");
}

function istTabellenZeichen(z: string): boolean {
  return MORSE_TABELLE[z] !== undefined && z !== "CH";
}

/** Ein Wort in Zeichen: die Zeichen und woher sie kommen — `"fuell"`: überhören, `null`: nicht zu lesen. */
export function deute(t: string, erlaubt: ReadonlySet<string> | undefined): { zeichen: string[]; quelle: Quelle } | "fuell" | null {
  const zw = suche(ZEICHENWOERTER, t);
  if (zw) return { zeichen: [zw], quelle: "sicher" };
  const zi = suche(ZIFFERNWOERTER, t);
  if (zi) return { zeichen: [zi], quelle: "sicher" };
  const bn = suche(BUCHSTABIERNAMEN, t, true);
  if (bn) return { zeichen: [bn], quelle: "sicher" };
  // Merkwörter („Monitor“ → M): nur als GANZES Wort, Eselsbrücken und Zusatzliste (shared/morse-sprech-merkwoerter.ts).
  const mw = merkwortBuchstabe(t.normalize("NFC"));
  if (mw) return { zeichen: [mw], quelle: "sicher" };
  const bu = suche(BUCHSTABENNAMEN, t) ?? suche(BUCHSTABENNAMEN, gestaucht(t));
  if (bu) return { zeichen: [bu], quelle: "buchstabe" };
  if ([...t].length === 1) {
    const z = grossZeichen(t);
    return istTabellenZeichen(z) ? { zeichen: [z], quelle: "buchstabe" } : null;
  }
  if (FUELLWOERTER.has(t)) return "fuell";
  // Mehrere Zeichen in einem Wort: nur, wenn jedes zu den Zeichen der Runde gehört.
  const alle = [...t].map(grossZeichen);
  if (alle.every(istTabellenZeichen) && (!erlaubt || alle.every((z) => erlaubt.has(z)))) return { zeichen: alle, quelle: "buchstabe" };
  return null;
}

/* ── Lesen ────────────────────────────────────────────────────────── */

interface LeseOptionen {
  /** So viele Zeichen passen noch (die Länge der Gruppe, abzüglich dessen, was schon im Feld steht). */
  laenge: number;
  /** Die Zeichen der Runde — für Verwechslungen und für Wörter mit mehreren Zeichen. */
  erlaubt?: ReadonlySet<string>;
}

/**
 * Ist ein Zeichen unsicher: schwache Konfidenz, oder ein Buchstabe, dessen Verwechsler in der Runde vorkommen? `t`: das gehörte
 * Wort — ein ausgeschriebener Name verwechselt nur, was gleich klingt (`verwechslerVon`).
 */
function unsicher(z: string, t: string, quelle: Quelle, konf: number | null, erlaubt: ReadonlySet<string> | undefined): string[] | null {
  // Ein unsicher gehörter Buchstabiername („Martha“ bei 0,2) bietet wie bisher alle Verwechsler an — dort sagt kein Selbstlaut etwas.
  const nachbarn = (quelle === "buchstabe" ? verwechslerVon(z, t) : (VERWECHSLUNG[z] ?? [])).filter((n) => !erlaubt || erlaubt.has(n));
  const schwach = konf !== null && konf < KONFIDENZ_UNSICHER;
  if (!schwach && !(quelle === "buchstabe" && nachbarn.length > 0)) return null;
  return [z, ...nachbarn].slice(0, 4);
}

/** Das Zeichen, oder eine Lücke mit den Möglichkeiten, wenn es unsicher ist. */
function waehle(z: string, t: string, quelle: Quelle, konf: number | null, erlaubt: ReadonlySet<string> | undefined): GelesenesZeichen {
  const moegliche = unsicher(z, t, quelle, konf, erlaubt);
  return moegliche ? { zeichen: null, moegliche } : { zeichen: z, moegliche: [] };
}

/** Aus dem Gesprochenen die Zeichen ziehen. Wirft nie; leerer Text ergibt ein leeres Ergebnis. */
export function leseGesprochen(eingabe: string | readonly SprachWort[], o: LeseOptionen): Gelesen {
  const stuecke = zerlege(eingabe);
  const alle: GelesenesZeichen[] = [];
  const unverstanden: string[] = [];
  for (let i = 0; i < stuecke.length; i++) {
    const { t, konf } = stuecke[i];
    // „Klammer auf“ / „Klammer zu“ sind zwei Wörter.
    if (t === "klammer") {
      const naechstes = stuecke[i + 1]?.t;
      if (naechstes === "auf" || naechstes === "zu") {
        alle.push({ zeichen: naechstes === "auf" ? "(" : ")", moegliche: [] });
        i++;
      }
      continue;
    }
    // „X-Ray“ sind zwei Wörter: das X davor ist der Buchstabiername, nicht ein einzelner (verwechselbarer) Buchstabe.
    if (t === "x" && stuecke[i + 1]?.t === "ray") {
      alle.push(waehle("X", "x", "sicher", konf, o.erlaubt));
      i++;
      continue;
    }
    const d = deute(t, o.erlaubt);
    // Nur EIN Wort gesagt, und es ist eine typische Fehlhörung („you“, „und“, „herr“): eine Lücke mit den Buchstaben zum Antippen.
    const fehl = stuecke.length === 1 && (d === "fuell" || d === null) ? fehlhoerung(t) : [];
    if (fehl.length) {
      const passend = o.erlaubt ? fehl.filter((z) => o.erlaubt!.has(z)) : [...fehl];
      alle.push({ zeichen: null, moegliche: (passend.length ? passend : [...fehl]).slice(0, 4) });
      continue;
    }
    if (d === "fuell") continue;
    if (d === null) {
      unverstanden.push(t);
      continue;
    }
    // Mehrere Zeichen in einem Wort („kmu“): jedes ist ein bloßer Buchstabe — alle seine Verwechsler gelten.
    for (const z of d.zeichen) alle.push(waehle(z, d.zeichen.length > 1 ? z.toLowerCase() : t, d.quelle, konf, o.erlaubt));
  }
  const platz = Math.max(0, Math.floor(o.laenge));
  const zeichen = alle.slice(0, platz);
  return {
    zeichen,
    feld: zeichen.map((z) => z.zeichen ?? LUECKE).join(""),
    abgeschnitten: Math.max(0, alle.length - platz),
    unverstanden,
    leer: alle.length === 0,
    luecken: zeichen.filter((z) => z.zeichen === null).length,
  };
}
